import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Swal from 'sweetalert2';

import 'bootstrap/dist/css/bootstrap.min.css';
import '../styles/formularios.css';
import '../styles/styles.css';
import logoInstituto from "../components/images/logo.png";
import logoSEP from "../components/images/SEP_Logo_2019.png";
import { useLocation, useNavigate } from 'react-router-dom';


// Si prefieres usar imports de imagen como en tu ejemplo, descomenta estas dos líneas y usa src={logoInstituto} / src={logoSEP}
// import logoInstituto from '../components/images/logo.png';
// import logoSEP from '../components/images/SEP_Logo_2019.svg.png';

const ModificarCategoriaMateria = () => {
  // estado UI
  const [opciones, setOpciones] = useState([]);          // [{Descripcion: 'Matemáticas'}, ...]
  const [seleccion, setSeleccion] = useState('');        // descripción elegida en el combo
  const [idCategoria, setIdCategoria] = useState('');    // IdCategoriaMateria actual
  const [descripcion, setDescripcion] = useState('');    // descripción editable
  const [loading, setLoading] = useState(false);

  const apiBases = useMemo(() => {
    const list = [];
    const seen = new Set();
    const pushBase = (value) => {
      if (!value) return;
      const normalized = value.replace(/\/$/, '');
      if (!seen.has(normalized)) {
        seen.add(normalized);
        list.push(normalized);
      }
    };

    const pushVariants = (value, { includeRaw = false } = {}) => {
      if (!value) return;
      const trimmed = value.replace(/\/$/, '');
      if (includeRaw) pushBase(trimmed);
      pushBase(`${trimmed}/api`);
      pushBase(`${trimmed}/backend`);
    };

    const envBase = process.env.REACT_APP_API_BASE?.trim();
    if (envBase) pushVariants(envBase, { includeRaw: true });

    if (typeof window !== 'undefined') {
      const { origin } = window.location;
      pushVariants(origin);
      pushVariants('https://institutosilvianocarrillo.com');
      pushVariants('https://www.institutosilvianocarrillo.com');
    }

    pushBase('/api');
    pushBase('/backend');

    return list.map((base) => `${base}/categorias`);
  }, []);
  const navigate = useNavigate();
    const location = useLocation();
    const handleSalir = () => {
      const rol = resolveRolBase(location.pathname);
      if (rol) {
        navigate(`/${rol}/inicio`, { replace: true });
      } else {
        // Fallback por si no se detecta rol ni en path ni en localStorage:
        navigate('/inicio', { replace: true });
      }
    };

  // Cargar combo al iniciar
  const cargarCategoriasCombo = useCallback(async () => {
    try {
      let lastError = null;
      for (const base of apiBases) {
        const url = `${base}/categorias-nombres`;
        try {
          const res = await fetch(url);
          const text = await res.text();

          if (!res.ok) {
            lastError = new Error(text || `Error ${res.status}`);
            lastError.status = res.status;
            console.warn(`⚠️ Falló la petición a ${url}:`, lastError);
            continue;
          }

          const contentType = res.headers.get('content-type') || '';
          const trimmed = text.trim();
          if (!trimmed) {
            setOpciones([]);
            return;
          }

          if (contentType.includes('application/json') || trimmed.startsWith('{') || trimmed.startsWith('[')) {
            const data = JSON.parse(trimmed);
            setOpciones(Array.isArray(data) ? data : []);
            return;
          }

          lastError = new Error(`Respuesta inválida desde ${url}`);
          console.warn(`⚠️ Contenido no JSON desde ${url} (content-type: ${contentType || 'desconocido'})`);
        } catch (error) {
          lastError = error;
          console.warn(`⚠️ Error de red al solicitar ${url}:`, error);
        }
      }

      throw lastError || new Error('No se pudieron cargar las categorías');
    } catch (err) {
      console.error('Error al cargar combo:', err);
      setOpciones([]);
      await Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'No se pudieron cargar las categorías',
      });
    }
  }, [apiBases]);

  useEffect(() => {
    cargarCategoriasCombo();
  }, [cargarCategoriasCombo]);

  // Cuando el usuario elige una descripción, traemos ID + descripción actual
  const handleSeleccionCategoria = async (e) => {
    const desc = e.target.value;
    setSeleccion(desc);

    // limpiar si no hay selección
    if (!desc) {
      setIdCategoria('');
      setDescripcion('');
      return;
    }

    try {
      let lastError = null;
      for (const base of apiBases) {
        const url = `${base}/categoria-por-nombre/${encodeURIComponent(desc)}`;
        try {
          const res = await fetch(url);
          const text = await res.text();

          if (!res.ok) {
            lastError = new Error(text || `Error ${res.status}`);
            lastError.status = res.status;
            console.warn(`⚠️ Falló la petición a ${url}:`, lastError);
            continue;
          }

          const contentType = res.headers.get('content-type') || '';
          const trimmed = text.trim();
          if (!trimmed) {
            break;
          }

          if (contentType.includes('application/json') || trimmed.startsWith('{') || trimmed.startsWith('[')) {
            const data = JSON.parse(trimmed);
            if (data && data.IdCategoriaMateria) {
              setIdCategoria(String(data.IdCategoriaMateria));
              setDescripcion(data.Descripcion ?? '');
            } else {
              setIdCategoria('');
              setDescripcion('');
              await Swal.fire({
                icon: 'warning',
                title: 'Sin datos',
                text: 'No se encontró la categoría seleccionada',
              });
            }
            return;
          }

          lastError = new Error(`Respuesta inválida desde ${url}`);
          console.warn(`⚠️ Contenido no JSON desde ${url} (content-type: ${contentType || 'desconocido'})`);
        } catch (error) {
          lastError = error;
          console.warn(`⚠️ Error de red al solicitar ${url}:`, error);
        }
      }

      throw lastError || new Error('No se pudo consultar la categoría');
    } catch (err) {
      console.error('Error al obtener categoría:', err);
      await Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'No se pudo consultar la categoría',
      });
    }
  };

  const guardarCambios = async () => {
    if (!idCategoria || !descripcion.trim()) {
      await Swal.fire({
        icon: 'warning',
        title: 'Faltan datos',
        text: 'Selecciona una categoría y escribe la nueva descripción.',
      });
      return;
    }

    setLoading(true);
    try {
      let lastError = null;
      for (const base of apiBases) {
        const url = `${base}/modificar`;
        try {
          const res = await fetch(url, {
            method: 'POST', // tu backend acepta POST para modificar
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              id: idCategoria,
              descripcionNueva: descripcion.trim(),
            }),
          });

          const text = await res.text();
          if (!res.ok) {
            lastError = new Error(text || `Error ${res.status}`);
            lastError.status = res.status;
            console.warn(`⚠️ Falló el POST a ${url}:`, lastError);
            continue;
          }

          await Swal.fire({
            icon: 'success',
            title: 'Modificado',
            text: text || 'Categoría modificada correctamente',
            timer: 2000,
            showConfirmButton: false,
          });

          await cargarCategoriasCombo();
          setSeleccion(descripcion.trim());
          return;
        } catch (error) {
          lastError = error;
          console.warn(`⚠️ Error de red al hacer POST ${url}:`, error);
        }
      }

      throw lastError || new Error('No se pudo modificar la categoría');
    } catch (err) {
      console.error('Error al modificar:', err);
      await Swal.fire({
        icon: 'error',
        title: 'Error',
        text: err.message || 'No se pudo modificar la categoría',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container py-4 px-4">
      {/* Encabezado */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <img src={logoInstituto} alt="Logo Instituto" className="logo" />
        <div className="flex-grow-1 text-center">
          <h4 className="mb-0">Instituto Silviano Carrillo</h4>
          <small className="text-muted fs-5">Modificar Categoría de Materia</small>
        </div>
        <img src={logoSEP} alt="Logo SEP" className="logo" />
      </div>


      {/* ComboBox */}
      <div className="mb-4">
        <label htmlFor="selectCategoria" className="form-label fw-semibold">
          Selecciona la categoría a modificar:
        </label>
        <select
          className="form-select"
          id="selectCategoria"
          value={seleccion}
          onChange={handleSeleccionCategoria}
        >
          <option value="">Selecciona una categoría</option>
          {opciones.map((c) => (
            <option key={c.Descripcion} value={c.Descripcion}>
              {c.Descripcion}
            </option>
          ))}
        </select>
      </div>

      {/* Formulario */}
      <div className="mb-4 p-4 rounded bg-light shadow-sm">
        <h6 className="mb-3">Categoría de Materia</h6>
        <form className="row g-3" onSubmit={(e) => e.preventDefault()}>
          <div className="col-md-2">
            <label htmlFor="idCategoria" className="form-label">ID</label>
            <input
              type="text"
              className="form-control"
              id="idCategoria"
              placeholder="Auto"
              value={idCategoria}
              disabled
            />
          </div>
          <div className="col-md-8">
            <label htmlFor="descripcion" className="form-label">Descripción</label>
            <input
              type="text"
              className="form-control"
              id="descripcion"
              placeholder="Nueva descripción..."
              value={descripcion}
              onChange={(e) => setDescripcion(e.target.value)}
            />
          </div>
        </form>
      </div>

      {/* Botones */}
      <div className="d-flex justify-content-center gap-4">
        <button
          className="btn btn-success px-4"
          onClick={guardarCambios}
          disabled={loading}
        >
          {loading ? 'Guardando...' : 'Guardar'}
        </button>
        <button type="button" className="btn btn-danger px-4" onClick={handleSalir}>
             Salir
        </button>
      </div>
    </div>
  );
};
/* ----------------- Helper para detectar el rol ----------------- */
function resolveRolBase(pathname) {
  // 1) Intenta desde la URL actual
  const roles = ['directora', 'coordinadora', 'docente', 'alumno'];
  const seg = pathname.split('/').filter(Boolean)[0];
  if (roles.includes(seg)) return seg;

  // 2) Respaldo desde localStorage.role (números mapeados a slugs)
  const roleId = Number(localStorage.getItem('role'));
  const map = { 1: 'directora', 2: 'coordinadora', 3: 'docente', 4: 'alumno' };
  return map[roleId] || null;
}

export default ModificarCategoriaMateria;
